import { useMutation } from '@apollo/client'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { UPDATE_FEATURE_FLAG } from 'data/layoutcreator/mutations/featureFlags'
import { GET_FEATURE_FLAGS } from 'data/layoutcreator/queries/featureFlags'
import IslandModal from 'shared/components/modals/IslandModal'
import { FeatureFlagState } from 'shared/types/layoutcreator/graphql'
import Button from 'shared/ui/Button_2'
import Flex from 'shared/ui/Flex'
import Input from 'shared/ui/Input'
import Text from 'shared/ui/Text'

import { GroupList, GroupRow } from './styles'

const ManageGroupsModal = ({
  isOpen,
  onClose,
  flagKey,
  flagState,
  initialGroupIds
}: {
  isOpen: boolean
  onClose: () => void
  flagKey: string
  flagState: FeatureFlagState
  initialGroupIds: string[]
}) => {
  const [groupIdsInput, setGroupIdsInput] = useState('')
  const [localGroupIds, setLocalGroupIds] = useState<string[]>([])

  useEffect(() => {
    if (isOpen) {
      setLocalGroupIds(initialGroupIds)
      setGroupIdsInput('')
    }
  }, [isOpen])

  const [updateFeatureFlag] = useMutation(UPDATE_FEATURE_FLAG, {
    refetchQueries: [GET_FEATURE_FLAGS]
  })

  const handleAddGroups = async () => {
    const newGroupIds = groupIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(Boolean)

    if (!newGroupIds.length) return

    const { data } = await updateFeatureFlag({
      variables: {
        input: {
          key: flagKey,
          state: flagState,
          limitedGroupIds: [...localGroupIds, ...newGroupIds]
        }
      }
    })

    if (data?.updateFeatureFlag) {
      setLocalGroupIds(data.updateFeatureFlag.limitedGroupIds)
      setGroupIdsInput('')
      toast.success('Groups added')
    } else {
      toast.error('Failed to add groups')
    }
  }

  const handleRemoveGroup = async (groupId: string) => {
    const newIds = localGroupIds.filter(id => id !== groupId)

    const { data } = await updateFeatureFlag({
      variables: {
        input: {
          key: flagKey,
          state: flagState,
          limitedGroupIds: newIds
        }
      }
    })

    if (data?.updateFeatureFlag) {
      setLocalGroupIds(data.updateFeatureFlag.limitedGroupIds)
    } else {
      toast.error('Failed to remove group')
    }
  }

  return (
    <IslandModal
      isOpen={isOpen}
      closeModal={onClose}
      width={800}
      title="Manage feature flag groups"
    >
      <Flex direction="column">
        <Flex columnGap={16} alignItems="center">
          <Flex flex={1}>
            <Input
              placeholder="Add group ids (comma-separated)"
              value={groupIdsInput}
              onChange={setGroupIdsInput}
              fullWidth
              size="small"
            />
          </Flex>
          <Button
            textId="Add"
            intent="primary-brand-02"
            onPress={handleAddGroups}
            size="sm"
          />
        </Flex>

        <Text
          text="Select groups to manage"
          size={18}
          weight={600}
          marginTop={46}
        />
        <GroupList>
          {localGroupIds.map(groupId => (
            <GroupRow key={groupId}>
              <Text text={groupId} />
              <Button
                textId="Remove"
                intent="destructive"
                onPress={() => handleRemoveGroup(groupId)}
                size="sm"
              />
            </GroupRow>
          ))}
        </GroupList>
      </Flex>
    </IslandModal>
  )
}

export default ManageGroupsModal
